import type { SearchExplanationField } from "@/lib/blog/search-types";
import {
  buildSearchExplanationTerms,
  collectSearchExplanationMatches,
} from "@/lib/blog/search-lexical";

export function normalizeSearchSnippetText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

export function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeSearchTermForBoundaryMatch(term: string): string | null {
  const normalized = term.trim().toLowerCase();
  return normalized.length > 1 ? normalized : null;
}

export function findFirstWholeTermMatchIndex({
  explanationTerms,
  text,
  queryTerms,
}: {
  explanationTerms?: readonly string[];
  text: string;
  queryTerms: readonly string[];
}): number {
  const lower = text.toLowerCase();
  const seen = new Set<string>();
  const effectiveExplanationTerms =
    explanationTerms && explanationTerms.length > 0
      ? explanationTerms
      : buildSearchExplanationTerms(queryTerms);
  let firstMatch = -1;

  for (const rawTerm of effectiveExplanationTerms) {
    const term = normalizeSearchTermForBoundaryMatch(rawTerm);
    if (!term || seen.has(term)) continue;
    seen.add(term);

    const regex = new RegExp(
      `(^|[^a-z0-9])(${escapeRegex(term)})(?=$|[^a-z0-9])`,
      "i"
    );
    const match = regex.exec(lower);
    if (!match) continue;

    const boundaryPrefixLength = match[1]?.length ?? 0;
    const startIndex = match.index + boundaryPrefixLength;
    if (firstMatch === -1 || startIndex < firstMatch) {
      firstMatch = startIndex;
    }
  }

  return firstMatch;
}

export function collectMatchedTerms({
  explanationTerms,
  text,
  queryTerms,
}: {
  explanationTerms?: readonly string[];
  text: string;
  queryTerms: readonly string[];
}): string[] {
  const effectiveExplanationTerms =
    explanationTerms && explanationTerms.length > 0
      ? explanationTerms
      : buildSearchExplanationTerms(queryTerms);
  return collectSearchExplanationMatches({
    explanationTerms: effectiveExplanationTerms,
    text,
  });
}

export function selectSnippetSource({
  title,
  description,
  explanationTerms,
  preferredFields,
  textIndex,
  textSnippet,
  queryTerms,
}: {
  title: string;
  description: string;
  explanationTerms?: readonly string[];
  preferredFields?: readonly SearchExplanationField[];
  textIndex: string;
  textSnippet: string;
  queryTerms: readonly string[];
}): {
  snippetSource: string;
  matchedTerms: string[];
} {
  const contentSource = textSnippet || textIndex;
  const fallbackSource = description || title || contentSource || "";
  const effectiveExplanationTerms =
    explanationTerms && explanationTerms.length > 0
      ? explanationTerms
      : buildSearchExplanationTerms(queryTerms);

  if (queryTerms.length === 0) {
    return {
      snippetSource: fallbackSource,
      matchedTerms: [],
    };
  }

  const titleMatches =
    title.length > 0
      ? collectMatchedTerms({
          explanationTerms: effectiveExplanationTerms,
          text: title,
          queryTerms,
        })
      : [];
  const descriptionMatches =
    description.length > 0
      ? collectMatchedTerms({
          explanationTerms: effectiveExplanationTerms,
          text: description,
          queryTerms,
        })
      : [];
  const contentMatches =
    contentSource.length > 0
      ? collectMatchedTerms({
          explanationTerms: effectiveExplanationTerms,
          text: contentSource,
          queryTerms,
        })
      : [];

  const fieldEntries = [
    {
      field: "title" satisfies SearchExplanationField,
      matchedTerms: titleMatches,
      source: title,
    },
    {
      field: "description" satisfies SearchExplanationField,
      matchedTerms: descriptionMatches,
      source: description,
    },
    {
      field: "body" satisfies SearchExplanationField,
      matchedTerms: contentMatches,
      source: contentSource,
    },
  ];

  const preferredMatch =
    preferredFields?.find((field) =>
      fieldEntries.some(
        (entry) => entry.field === field && entry.matchedTerms.length > 0
      )
    ) ?? null;

  if (preferredMatch) {
    const preferredEntry = fieldEntries.find((entry) => entry.field === preferredMatch);
    if (preferredEntry && preferredEntry.source) {
      return {
        snippetSource: preferredEntry.source,
        matchedTerms: preferredEntry.matchedTerms.slice(0, 3),
      };
    }
  }

  const summarySnippetSource =
    titleMatches.length === 0 && descriptionMatches.length === 0
      ? fallbackSource
      : descriptionMatches.length > titleMatches.length
      ? description
      : titleMatches.length > descriptionMatches.length
      ? title
      : description || title;

  const summaryMatchedTerms =
    summarySnippetSource === description
      ? descriptionMatches
      : summarySnippetSource === title
      ? titleMatches
      : [];

  const useContentSnippet =
    contentMatches.length > summaryMatchedTerms.length ||
    (summaryMatchedTerms.length === 0 && contentMatches.length > 0);

  return {
    snippetSource: useContentSnippet ? contentSource : summarySnippetSource,
    matchedTerms: (useContentSnippet ? contentMatches : summaryMatchedTerms).slice(
      0,
      3
    ),
  };
}

export function buildSnippetWindow({
  explanationTerms,
  text,
  queryTerms,
  maxLength = 180,
}: {
  explanationTerms?: readonly string[];
  text: string;
  queryTerms: readonly string[];
  maxLength?: number;
}): string {
  const compact = normalizeSearchSnippetText(text);
  if (compact.length <= maxLength) return compact;

  const firstMatch = findFirstWholeTermMatchIndex({
    explanationTerms,
    text: compact,
    queryTerms,
  });

  if (firstMatch === -1) {
    return `${compact.slice(0, maxLength).trimEnd()}…`;
  }

  const start = Math.max(0, firstMatch - 48);
  const end = Math.min(compact.length, start + maxLength);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < compact.length ? "…" : "";
  return `${prefix}${compact.slice(start, end).trim()}${suffix}`;
}
